// All projects: search and filters that work with or without JavaScript
// (a real GET form), results that update as you choose, a bottom-sheet
// filter on a phone, sorting, and an optional map.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useSite } from '../lib/site-context.jsx';
import { toRupees } from '../lib/format.js';
import { ProjectCard, EmptyState } from '../ui/ProjectCard.jsx';
import { Icon } from '../ui/Icon.jsx';

const FIELDS = ['search', 'city', 'project_type', 'status', 'price_max_gte', 'price_min_lte', 'unit_types'];
const UNIT_TYPES = [['Studio', 'Studio'], ['1BHK', '1 BHK'], ['2BHK', '2 BHK'], ['3BHK', '3 BHK'], ['4BHK', '4 BHK'], ['4+BHK', '4+ BHK'], ['Villa', 'Villa'], ['Plot', 'Plot']];
const STATUSES = ['Pre-launch', 'Under Construction', 'Ready to Move'];
const SORTS = [['featured', 'Featured'], ['price_asc', 'Price: low to high'], ['price_desc', 'Price: high to low'], ['name', 'Name A–Z']];

const cleanValues = (v) => Object.fromEntries(Object.entries(v).filter(([k, val]) => FIELDS.includes(k) && String(val ?? '').trim() !== ''));
const priceOf = (p) => toRupees(p.price_min ?? p.price_max, p.price_unit_label) ?? Infinity;

function sortProjects(list, sort) {
  if (sort === 'price_asc') return [...list].sort((a, b) => priceOf(a) - priceOf(b));
  if (sort === 'price_desc') return [...list].sort((a, b) => (priceOf(b) === Infinity ? -1 : priceOf(b)) - (priceOf(a) === Infinity ? -1 : priceOf(a)));
  if (sort === 'name') return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return list;
}

// Leaflet + OpenStreetMap (free, no key), loaded only when the map is opened.
let leafletLoading = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!leafletLoading) {
    leafletLoading = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(css);
      const js = document.createElement('script');
      js.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      js.onload = () => resolve(window.L);
      js.onerror = () => { leafletLoading = null; reject(new Error('The map could not be loaded.')); };
      document.head.appendChild(js);
    });
  }
  return leafletLoading;
}

function MapView({ projects }) {
  const box = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let gone = false;
    loadLeaflet().then((L) => {
      if (gone || !box.current) return;
      if (!map.current) {
        map.current = L.map(box.current).setView([28.5, 77.3], 9);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors', maxZoom: 18 }).addTo(map.current);
        layer.current = L.layerGroup().addTo(map.current);
      }
      layer.current.clearLayers();
      const withCoords = projects.filter((p) => p.latitude && p.longitude);
      for (const p of withCoords) {
        const popup = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = p.name;
        const link = document.createElement('a');
        link.href = `/project?id=${encodeURIComponent(p.id)}`;
        link.textContent = 'View details';
        popup.append(title, document.createElement('br'), link);
        L.marker([p.latitude, p.longitude]).bindPopup(popup).addTo(layer.current);
      }
      setTimeout(() => {
        map.current.invalidateSize();
        if (withCoords.length) map.current.fitBounds(L.latLngBounds(withCoords.map((p) => [p.latitude, p.longitude])), { padding: [30, 30], maxZoom: 13 });
      }, 0);
    }).catch((e) => setError(e.message));
    return () => { gone = true; };
  }, [projects]);
  useEffect(() => () => { map.current?.remove(); map.current = null; }, []);
  return error ? <EmptyState>{error}</EmptyState> : <div className="map-view" id="map-view" ref={box} role="region" aria-label="Map of projects" />;
}

export default function Projects({ data }) {
  const { t } = useSite();
  const { options } = data;
  const [values, setValues] = useState(data.filters || {});
  const [results, setResults] = useState(data.projects || []);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [sort, setSort] = useState('featured');
  const [sheet, setSheet] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const seq = useRef(0);
  const timer = useRef(null);
  const first = useRef(true);
  const sheetRef = useRef(null);

  const load = useCallback(async (v) => {
    const mine = ++seq.current;
    const query = new URLSearchParams(cleanValues(v));
    window.history.replaceState(null, '', query.toString() ? `?${query}` : window.location.pathname);
    setLoading(true);
    setFailed(false);
    try {
      const list = await api(`/api/projects${query.toString() ? `?${query}` : ''}`);
      if (mine === seq.current) setResults(list);
    } catch { if (mine === seq.current) setFailed(true); } finally { if (mine === seq.current) setLoading(false); }
  }, []);

  // Selects apply at once; typing waits for a pause.
  const change = (name, value, { now = false } = {}) => {
    const next = { ...values, [name]: value };
    setValues(next);
    clearTimeout(timer.current);
    if (now) load(next); else timer.current = setTimeout(() => load(next), 350);
  };
  useEffect(() => { first.current = false; return () => clearTimeout(timer.current); }, []);

  const reset = () => { setValues({}); load({}); };
  const active = Object.keys(cleanValues(values));
  const activeCount = active.filter((k) => k !== 'search').length;
  const shown = useMemo(() => sortProjects(results, sort), [results, sort]);

  // The phone's filter sheet: Escape closes, the page behind stays still.
  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSheet(false); };
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('sheet-open');
    sheetRef.current?.querySelector('select, input')?.focus();
    return () => { document.removeEventListener('keydown', onKey); document.documentElement.classList.remove('sheet-open'); };
  }, [sheet]);

  const chips = active.map((k) => ({ k, label: k === 'price_max_gte' ? `Min ₹${values[k]} Cr` : k === 'price_min_lte' ? `Max ₹${values[k]} Cr` : k === 'search' ? `“${values[k]}”` : values[k] }));

  return (
    <>
      <section className="section page-head">
        <div className="wrap">
          <span className="section-eyebrow">{t('heading_all_listings', 'All listings')}</span>
          <h1>{t('heading_projects_title', 'Projects across Noida, Gurugram & the Yamuna Expressway')}</h1>
          <p className="page-lede">{t('projects_sub', 'Filter by corridor, project type or status to find a RERA-registered listing that matches your brief.')}</p>
        </div>
      </section>

      <section className="section section-flush">
        <div className="wrap">
          <form className="filter-bar" id="filter-form" action="/projects" method="get" onSubmit={(e) => { e.preventDefault(); clearTimeout(timer.current); load(values); setSheet(false); }}>
            <div className="filter-top">
              <div className="field search">
                <label htmlFor="f-search" className="sr-only">{t('label_search', 'Search')}</label>
                <Icon name="search" size={18} className="field-ico" />
                <input id="f-search" name="search" type="search" value={values.search || ''} placeholder={t('placeholder_search', 'Project name, sector, locality…')} onChange={(e) => change('search', e.target.value)} autoComplete="off" />
              </div>
              <button type="button" className="btn btn-outline filters-open" aria-haspopup="dialog" aria-expanded={sheet} onClick={() => setSheet(true)}>
                <Icon name="filter" size={18} /> {t('filters', 'Filters')}{activeCount > 0 && <span className="count-badge">{activeCount}</span>}
              </button>
            </div>

            <div className={`filter-panel${sheet ? ' is-open' : ''}`} id="filter-panel" ref={sheetRef} role={sheet ? 'dialog' : undefined} aria-modal={sheet ? 'true' : undefined} aria-label={t('filters', 'Filters')}>
              <div className="filter-panel-head">
                <h2>{t('filters', 'Filters')}</h2>
                <button type="button" className="icon-btn" aria-label={t('close', 'Close')} onClick={() => setSheet(false)}><Icon name="close" size={22} /></button>
              </div>
              <div className="filter-fields">
                <div className="field">
                  <label htmlFor="f-city">{t('label_corridor', 'Corridor')}</label>
                  <select id="f-city" name="city" value={values.city || ''} onChange={(e) => change('city', e.target.value, { now: true })}>
                    <option value="">{t('option_all_corridors', 'All corridors')}</option>
                    {options.cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="f-type">{t('label_project_type', 'Project type')}</label>
                  <select id="f-type" name="project_type" value={values.project_type || ''} onChange={(e) => change('project_type', e.target.value, { now: true })}>
                    <option value="">{t('option_all_types', 'All types')}</option>
                    {options.types.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="f-status">{t('label_status', 'Status')}</label>
                  <select id="f-status" name="status" value={values.status || ''} onChange={(e) => change('status', e.target.value, { now: true })}>
                    <option value="">{t('option_any_status', 'Any status')}</option>
                    {STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="f-unit-types">{t('label_bedrooms', 'Bedrooms / type')}</label>
                  <select id="f-unit-types" name="unit_types" value={values.unit_types || ''} onChange={(e) => change('unit_types', e.target.value, { now: true })}>
                    <option value="">{t('any', 'Any')}</option>
                    {UNIT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="f-price-min">{t('label_min_price', 'Min price (₹ Cr)')}</label>
                  <input id="f-price-min" name="price_max_gte" type="number" inputMode="decimal" min="0" step="0.5" placeholder={t('any', 'Any')} value={values.price_max_gte || ''} onChange={(e) => change('price_max_gte', e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="f-price-max">{t('label_max_price', 'Max price (₹ Cr)')}</label>
                  <input id="f-price-max" name="price_min_lte" type="number" inputMode="decimal" min="0" step="0.5" placeholder={t('any', 'Any')} value={values.price_min_lte || ''} onChange={(e) => change('price_min_lte', e.target.value)} />
                </div>
              </div>
              <div className="filter-actions">
                <button type="submit" className="btn btn-rust">{t('apply_filters', 'Apply Filters')}</button>
                <button type="button" className="btn btn-ghost" onClick={reset} disabled={!active.length}>{t('reset', 'Reset')}</button>
              </div>
            </div>
            {sheet && <button type="button" className="sheet-scrim" aria-label={t('close', 'Close')} tabIndex={-1} onClick={() => setSheet(false)} />}
          </form>

          {chips.length > 0 && (
            <ul className="active-filters" aria-label="Active filters">
              {chips.map(({ k, label }) => (
                <li key={k}><button type="button" className="chip chip-x" onClick={() => change(k, '', { now: true })} aria-label={`Remove filter ${label}`}>{label} <Icon name="close" size={14} /></button></li>
              ))}
              <li><button type="button" className="link-btn" onClick={reset}>{t('reset_all', 'Clear all')}</button></li>
            </ul>
          )}

          <div className="results-bar">
            <div className="results-count" id="results-count" aria-live="polite">{shown.length === 0 ? '0 projects found' : `${shown.length} project${shown.length === 1 ? '' : 's'} found`}</div>
            <div className="results-tools">
              <label className="sort-field"><span className="sr-only">{t('sort', 'Sort')}</span>
                <select id="sort" value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t('sort', 'Sort')}>
                  {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <button type="button" className="btn btn-outline btn-sm" id="toggle-map-view" aria-pressed={showMap} onClick={() => setShowMap((m) => !m)}><Icon name="map" size={16} /> {showMap ? 'Grid View' : 'Map View'}</button>
              <a className="btn btn-outline btn-sm" href="/compare">{t('nav_compare', 'Compare')}</a>
            </div>
          </div>

          {showMap && <MapView projects={shown} />}
          {failed
            ? <EmptyState>Could not load projects right now. Please try again shortly.</EmptyState>
            : shown.length === 0
              ? <EmptyState action={active.length ? <button type="button" className="btn btn-outline btn-sm" onClick={reset}>{t('reset_all', 'Clear all')}</button> : null}>No projects match those filters. Try widening your search.</EmptyState>
              : <div className="project-grid" id="project-grid" aria-busy={loading}>{shown.map((p, i) => <ProjectCard key={p.id} project={p} priority={i < 3} />)}</div>}
        </div>
      </section>
    </>
  );
}
